import React from "react";
import PropTypes from "prop-types";
import ScanRow from "./ScanRow";
import SelectedDataDisplay from "./SelectedDataDisplay";
import "./ScannedData.css";

export default function ScannedDataView({
  scans,
  selectedData,
  setSelectedData,
  filterStatus,
  setFilterStatus,
  statusModal,
  setStatusModal,
  user,
  viewUserID,
  isAdmin,
  onStartScan,
  onOpenPhotoCapture,
  onOpenAddressEditor,
  onBackToUserSelection,
  userPath,
  isViewingOtherUser,
  handleRequestUpload,
  handleEditAddress,
  handleInitiateStatusUpdate,
  handleConfirmStatusUpdate,
  handleCancelStatusUpdate,
  handleSignOut,
  getScanStatus,
}) {
  return (
    <div>
      <h2 id="scanned-data-title">Scanned Data</h2>
      <div id="button-container">
        <button id="add-scan-button" onClick={onStartScan}>Add Scan</button>
        <button id="signout-button" onClick={handleSignOut}>Sign Out</button>
      </div>
      <div className="filter-wrapper" style={{ textAlign: "center", marginBottom: "15px" }}>
        <div className="filter-inner" style={{ display: "inline-block" }}>
          <div className="toggle-track">
            {[
              "all",
              "verified",
              "disputed",
              "rejected",
            ].map((stat) => (
              <button
                key={stat}
                onClick={() => setFilterStatus(stat)}
                className={`toggle-option ${filterStatus === stat ? "active" : ""}`}
                data-value={stat}
              >
                {stat.charAt(0).toUpperCase() + stat.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isAdmin && viewUserID && (
        <div className="admin-mode-banner">
          <div className="admin-user-id">
            <strong>{viewUserID}</strong>
          </div>
          <button onClick={onBackToUserSelection} className="admin-back-button">
            ← Back to User Selection
          </button>
        </div>
      )}

      <div className="table-wrapper">
        <table id="scanned-data-table">
          <thead>
            <tr>
              <th>Address</th>
              <th>Date</th>
              <th>Time</th>
              <th>Pic Taken</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {scans
              .filter((scan) => {
                if (filterStatus === "all") return true;
                const currentStatus = getScanStatus(scan);
                return currentStatus === filterStatus;
              })
              .map((scan) => (
                <ScanRow
                  key={scan.key}
                  scan={scan}
                  isSelected={selectedData?.key === scan.key}
                  onSelect={(scanToSelect) =>
                    setSelectedData((prev) =>
                      prev?.key === scanToSelect.key ? null : scanToSelect
                    )
                  }
                  onRequestUpload={handleRequestUpload}
                  onEditAddress={handleEditAddress}
                  isAdmin={isAdmin}
                  isViewingOtherUser={isViewingOtherUser}
                  status={getScanStatus(scan)}
                  onUpdateStatus={handleInitiateStatusUpdate}
                />
              ))}
          </tbody>
        </table>
      </div>

      <SelectedDataDisplay selectedData={selectedData} />

      {/* Status Update Modal */}
      {statusModal.open && (
        <div className="status-modal-overlay">
          <div className="status-modal">
            <h3>Update Status</h3>
            <div className="status-selection-group">
              <button
                className={`status-selection-btn status-selection-verified ${
                  statusModal.targetStatus === "verified" ? "selected" : ""
                }`}
                onClick={() =>
                  setStatusModal({ ...statusModal, targetStatus: "verified" })
                }
              >
                <span className="status-icon">✔</span>
                <span className="status-label">Verified</span>
              </button>
              <button
                className={`status-selection-btn status-selection-disputed ${
                  statusModal.targetStatus === "disputed" ? "selected" : ""
                }`}
                onClick={() =>
                  setStatusModal({ ...statusModal, targetStatus: "disputed" })
                }
              >
                <span className="status-icon">?</span>
                <span className="status-label">Disputed</span>
              </button>
              <button
                className={`status-selection-btn status-selection-rejected ${
                  statusModal.targetStatus === "rejected" ? "selected" : ""
                }`}
                onClick={() =>
                  setStatusModal({ ...statusModal, targetStatus: "rejected" })
                }
              >
                <span className="status-icon">✘</span>
                <span className="status-label">Rejected</span>
              </button>
            </div>
            <p className="status-modal-subtitle">
              {statusModal.targetStatus === "verified"
                ? "Note is optional."
                : "Note is required."}
            </p>
            <textarea
              className="status-modal-note"
              placeholder="Enter a note..."
              value={statusModal.note}
              onChange={(e) =>
                setStatusModal({ ...statusModal, note: e.target.value })
              }
            />
            <div className="status-modal-actions">
              <button className="status-modal-cancel" onClick={handleCancelStatusUpdate}>
                Cancel
              </button>
              <button className="status-modal-confirm" onClick={handleConfirmStatusUpdate}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

ScannedDataView.propTypes = {
  scans: PropTypes.array.isRequired,
  selectedData: PropTypes.object,
  setSelectedData: PropTypes.func.isRequired,
  filterStatus: PropTypes.string.isRequired,
  setFilterStatus: PropTypes.func.isRequired,
  statusModal: PropTypes.object.isRequired,
  setStatusModal: PropTypes.func.isRequired,
  user: PropTypes.object,
  viewUserID: PropTypes.string,
  isAdmin: PropTypes.bool,
  onStartScan: PropTypes.func.isRequired,
  onOpenPhotoCapture: PropTypes.func.isRequired,
  onOpenAddressEditor: PropTypes.func.isRequired,
  onBackToUserSelection: PropTypes.func.isRequired,
  userPath: PropTypes.string,
  isViewingOtherUser: PropTypes.bool,
  handleRequestUpload: PropTypes.func.isRequired,
  handleEditAddress: PropTypes.func.isRequired,
  handleInitiateStatusUpdate: PropTypes.func.isRequired,
  handleConfirmStatusUpdate: PropTypes.func.isRequired,
  handleCancelStatusUpdate: PropTypes.func.isRequired,
  handleSignOut: PropTypes.func.isRequired,
  getScanStatus: PropTypes.func.isRequired,
};
